import React, { FC, useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  PostAdd as PostAddIcon,
  Category as CategoryIcon,
  Badge as BadgeIcon,
  Place as PlaceIcon,
  ViewCarousel as ViewCarouselIcon,
  PermMedia as PermMediaIcon,
  AllInbox as AllInboxIcon,
  AccountCircle,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/img/logo.png';
import theme from '../utils/theme';
import { authName } from '../utils/config';
import AlertDialog from '../components/AlertDialog';
import { getUserData } from '../hooks/auth';

const menus = [
  { title: 'Posts', icon: <AllInboxIcon />, path: 'posts' },
  { title: 'Create New Post', icon: <PostAddIcon />, path: 'create-new-post' },
  { title: 'Categories', icon: <CategoryIcon />, path: 'categories' },
  { title: 'Visa Types', icon: <BadgeIcon />, path: 'visa-types' },
  { title: 'Locations', icon: <PlaceIcon />, path: 'locations' },
  { title: 'Carousel', icon: <ViewCarouselIcon />, path: 'carousel' },
  { title: 'Images', icon: <PermMediaIcon />, path: 'images' },
];

const drawerWidth = 240;

interface AdminLayoutProps {
  children: React.ReactNode;
  window?: () => Window;
}

const AdminLayout: FC<AdminLayoutProps> = ({ children, window }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await localStorage.removeItem(authName);
    navigate('/login');
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const drawer = (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img
          style={{
            width: '180px',
            padding: '5px 0px',
          }}
          src={logo}
          alt="clovermandalay"
        />
      </Box>
      <Divider />
      <List>
        {menus.map((menu, index) => (
          <Link to={menu.path} key={index} style={{ textDecoration: 'none', color: 'inherit' }}>
            <ListItem key={index} disablePadding>
              <ListItemButton
                sx={{
                  justifyContent: 'center',
                  px: 2.5,
                }}
              >
                <Paper
                  elevation={location.pathname.includes(menu.path) ? 1 : 4}
                  sx={{
                    mr: 2,
                    p: 1,
                    pb: 0.5,
                    borderRadius: '15%',
                    backgroundColor: location.pathname.includes(menu.path) ? theme.palette.primary.main : '',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: location.pathname.includes(menu.path) ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText,
                    }}
                  >
                    {menu.icon}
                  </ListItemIcon>
                </Paper>
                <ListItemText primary={menu.title} />
              </ListItemButton>
            </ListItem>
          </Link>
        ))}
      </List>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Admin Dashboard
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ mr: 3 }}>{getUserData() || ""}</Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 0,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 15,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  setShowLogoutAlert(true);
                }}
              >
                Log Out
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 2, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {children}
      </Box>
      <AlertDialog
        toggle={showLogoutAlert}
        setToggle={setShowLogoutAlert}
        confirm={handleLogout}
        cancel={() => setShowLogoutAlert(false)}
        title="Confirm Logout"
        content="Are you sure you want to log out?"
        type="warning"
      />
    </Box>
  );
};

export default AdminLayout;